import { useState, useEffect, useCallback, useRef } from 'react';

export function useLocalStorage(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      return JSON.parse(stored);
    } catch {
      return defaultValue;
    }
  });

  // valueRef always holds the latest value so the beforeunload handler can
  // flush it synchronously even if the React effect hasn't run yet.
  const valueRef      = useRef(value);
  const dirtyRef      = useRef(false);
  const timerRef      = useRef(null);
  const keyRef        = useRef(key);
  const isMounted     = useRef(false);
  keyRef.current      = key;

  // Debounce writes: wait 150 ms after the last change before hitting localStorage.
  // This collapses rapid successive state updates into a single write per key.
  // Skip the first run — the value was just read from localStorage so there is
  // nothing to write back. Marking dirty on mount would cause beforeunload to
  // overwrite localStorage with stale React state if a reload happens within 150 ms
  // (e.g. after externally seeding localStorage in e2e tests).
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    dirtyRef.current  = true;
    valueRef.current  = value;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify(value));
        dirtyRef.current = false;
      } catch (e) {
        console.warn('localStorage write failed:', e);
      }
    }, 150);
    return () => clearTimeout(timerRef.current);
  }, [key, value]);

  // Flush any pending write immediately before the page unloads so that a
  // page.reload() (or browser refresh) doesn't lose the last edit.
  useEffect(() => {
    function flush() {
      if (dirtyRef.current) {
        try {
          localStorage.setItem(keyRef.current, JSON.stringify(valueRef.current));
          dirtyRef.current = false;
        } catch {}
      }
    }
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, []);

  const setValueSafe = useCallback((updater) => {
    setValue(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // Update refs synchronously so beforeunload can flush even if the
      // effect hasn't run yet (React effects run after paint, not immediately).
      valueRef.current = next;
      dirtyRef.current = true;
      return next;
    });
  }, []);

  return [value, setValueSafe];
}
